'use client';

import { ItemTrackingStatus, type RoadmapStatusWithResourceDTO } from '@/domain/roadmap/types';
import { useRoadmapEditStore } from '@/features/roadmap/store/roadmap';
import { Chip } from '@andritzot/metris-web-ui/data-display/chip';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { CircularProgress } from '@andritzot/metris-web-ui/feedback/progress/circular-progress';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { Stack } from '@andritzot/metris-web-ui/layout/stack';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { useCallback, useDeferredValue, useMemo } from 'react';

import { PlanningDataTable } from './PlanningDataTable';

interface PlanningViewProps {
  isLoading: boolean;
  hasSelectedItem: boolean;
  statusFilter?: ItemTrackingStatus[];
  qaFilter?: boolean[];
  selectedItemIds?: Set<number>;
  onSelectionChange?: (ids: Set<number>) => void;
  viewOnly?: boolean;
}

const EMPTY_STATUSES: RoadmapStatusWithResourceDTO[] = [];

export function PlanningView({
  isLoading,
  hasSelectedItem,
  statusFilter,
  qaFilter,
  selectedItemIds,
  onSelectionChange,
  viewOnly,
}: Readonly<PlanningViewProps>) {
  const statuses = useRoadmapEditStore((s) => s.editableRoadMap.type?.roadmapStatus ?? EMPTY_STATUSES);
  const itemName = useRoadmapEditStore((s) => s.editableRoadMap.item);

  const phasesWithItems = useMemo(() => statuses.filter((s) => s.roadmapStatusItems.length > 0), [statuses]);

  const filteredStatuses = useMemo(() => {
    const hasStatusFilter = !!statusFilter?.length;
    const hasQaFilter = !!qaFilter?.length;
    if (!hasStatusFilter && !hasQaFilter) return phasesWithItems;
    return phasesWithItems
      .map((phase) => ({
        ...phase,
        roadmapStatusItems: phase.roadmapStatusItems.filter((item) => {
          const statusOk = !hasStatusFilter || statusFilter?.includes(item.status);
          const qaOk = !hasQaFilter || qaFilter?.includes(item.qualityAssurance);
          return statusOk && qaOk;
        }),
      }))
      .filter((phase) => phase.roadmapStatusItems.length > 0);
  }, [phasesWithItems, statusFilter, qaFilter]);

  const deferredFilteredStatuses = useDeferredValue(filteredStatuses);

  const handleInlineEdit = useCallback(
    (itemId: number, field: 'status' | 'qualityAssurance', value: ItemTrackingStatus | boolean) => {
      const { editableRoadMap } = useRoadmapEditStore.getState();
      const updatedStatuses = editableRoadMap.type?.roadmapStatus?.map((s) => ({
        ...s,
        roadmapStatusItems: s.roadmapStatusItems.map((item) => {
          if (item.idRoadmapStatusItem !== itemId) return item;
          return { ...item, [field]: value, edited: true };
        }),
      }));

      if (updatedStatuses) {
        useRoadmapEditStore.setState({
          editableRoadMap: {
            ...editableRoadMap,
            type: editableRoadMap.type ? { ...editableRoadMap.type, roadmapStatus: updatedStatuses } : null,
          },
          isStoreDirty: true,
        });
      }
    },
    [],
  );

  if (!hasSelectedItem) {
    return (
      <Stack
        direction="row"
        spacing={1}
        sx={{ justifyContent: 'center', alignItems: 'center', py: 6, color: 'text.secondary' }}
      >
        <InfoOutlined fontSize="small" />
        <Typography variant="body2">Select an item to view its planning data.</Typography>
      </Stack>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2, flexShrink: 0 }}>
        <Chip label={itemName} color="primary" variant="outlined" size="small" sx={{ maxWidth: 'fit-content' }} />
      </Stack>
      {isLoading ? (
        <Stack sx={{ justifyContent: 'center', alignItems: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Stack>
      ) : (
        <Box
          sx={{
            opacity: deferredFilteredStatuses === filteredStatuses ? 1 : 0.6,
            transition: 'opacity 150ms',
            flex: 1,
            minHeight: 0,
          }}
        >
          <PlanningDataTable
            statuses={deferredFilteredStatuses}
            selectedItemIds={viewOnly ? undefined : selectedItemIds}
            onSelectionChange={viewOnly ? undefined : onSelectionChange}
            onItemEdit={handleInlineEdit}
            viewOnly={viewOnly}
            isLoading={deferredFilteredStatuses !== filteredStatuses}
          />
        </Box>
      )}
    </Box>
  );
}
